import { action } from "typesafe-actions";
import {
  ITask,
  ITaskFileUpload,
  ITaskFileUploadFetch,
} from "./task.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { getFilenameFromContentDisposition } from "../../helpers";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IOldBusinessTrackerState } from "../../views/tasks/BeforeCreate/OldBusinessTrackerDialog/OldBusinessTrackerDialog.types";
import { makeApiCall } from "../../helpers/postRequest";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

export const uploadTaskFile = async (
   files: ITaskFileUpload['attachments'],
  module_name: string,
  asPayload: {
    [key: string]: string | number | null;
  }
) => {
  let filesCount =0;
  const formdata = new FormData();
  for (const file of files) {
    // Only count/append entries that hold a real browser File blob. Existing
    // attachments hydrated from the API may carry a non-File value in `file`
    // (e.g. the string "APP"), which must NOT trigger an upload — otherwise
    // the server receives no valid file part and responds "No file uploaded.".
    if (file.file instanceof File) {
      filesCount++;
      formdata.append("files", file.file);
    }
  }
  formdata.append("module_name", module_name);
  formdata.append("as_payload", JSON.stringify(asPayload));
  if(filesCount === 0){
    return files;
  }
  const res = await api.post("/general/upload", formdata);
  const data: string[] = res.data.data;
  const finalList = [];
  let i = 0;
  if (data.length > 0) {
    for (const item of files) {
      const url = data[i];
      const obj = {...item};
      if (item.file instanceof File) {
        obj.file = null;
        obj.url = url;
        i++;
      }
      finalList.push(obj);
    }
    return finalList;
  }

  return files;
};


export const FETCH_TASKS_LIST_PROGRESS = "FETCH_TASKS_LIST_PROGRESS";
export const FETCH_TASKS_LIST_SUCCESS = "FETCH_TASKS_LIST_SUCCESS";
export const FETCH_TASKS_LIST_FAILED = "FETCH_TASKS_LIST_FAILED";
export const fetchTasksListProgress = () => action(FETCH_TASKS_LIST_PROGRESS);
export const fetchTasksListSuccess = (
  tasksList: ITask[],
  totalRecords: number
) => action(FETCH_TASKS_LIST_SUCCESS, { tasksList, totalRecords });
export const fetchTasksListFailed = () => action(FETCH_TASKS_LIST_FAILED);

export const fetchTasksListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    customerPolicyId?: number,
    subType?: string,
    groupType?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchTasksListProgress());
      
      let finalUrl = `/tasks/get-task?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&pageLimit=${limit}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/tasks/get-task?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (groupType && groupType !== "-1") {
        finalUrl += "&group_of=" + groupType;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      if (customerPolicyId) {
        finalUrl += "&customer_policy_id=" + customerPolicyId;
      }
      if (subType) {
        finalUrl += "&task_sub_type=" + subType;
      }
      const res = await api.get(finalUrl);
      const data: ITask[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchTasksListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchTasksListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_TASK_PROGRESS = "FETCH_TASK_PROGRESS";
export const FETCH_TASK_SUCCESS = "FETCH_TASK_SUCCESS";
export const FETCH_TASK_FAILED = "FETCH_TASK_FAILED";

export const fetchTaskProgress = () => action(FETCH_TASK_PROGRESS);
export const fetchTaskSuccess = (data: ITask) =>
  action(FETCH_TASK_SUCCESS, { data });
export const fetchTaskFailed = (errorMessage: string) =>
  action(FETCH_TASK_FAILED, { errorMessage });

export const fetchTaskAysnc =
  (taskCode: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchTaskProgress());
      const res = await api.get(`/tasks/get-task?task_code=${taskCode}`);
      const data: ITask[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchTaskSuccess(data[0]));
      } else {
        dispatch(
          fetchTaskFailed(
            "Oops! We couldn't find any records associated with your task number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchTaskFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertTaskAysnc =
  (
    task: ITask,
    initialData: ITask,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean, task?: ITask) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await makeApiCall({
        url: "/tasks/upsert-task",
        method: "POST",
        automation: {
          isUpdate: isUpdate,
          primaryFieldName: "task_code",
        }
      },initialData, task) 
  
      dispatch(
        showMessage({
          type: "success",
          message: "Task is saved succesfully!",
          displayAs: "snackbar",
        })
      );
      dispatch(
        fetchTaskSuccess({ ...task, task_code: res.data.data.task_code })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchTaskMultiFileUploadAysnc =
  (
    taskCode: string,
    onCallback: (isSuccess: boolean, data?: ITaskFileUploadFetch[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/tasks/get-tasks-multi-attachment?task_code=${taskCode}`
      );
      const data: ITaskFileUploadFetch[] = res.data.data;

      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertTaskMutliFileUploadAysnc =
  (
    data: ITaskFileUpload,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        transaction: "POLICY_CHANGE",
        sub_type: "GENERAL_CHANGE",
        task_code: data.task_code,
      };

      const updateFilesJson = await uploadTaskFile(
        data.attachments,
        "TASK",
        asPayload
      );
      await api.post(`/tasks/upsert-tasks-multi-attachment`, {
        ...data,
        attachments: updateFilesJson,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "General change is saved succesfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const previewPoliciesCancellationListPDFAsync =
  (
    taskCode: number,
    // onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-cancellation-and-reinstatement-pdf?task_code=${taskCode}&is_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
      // onCallback();
    } catch (err: any) {
      dispatch(fetchTasksListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const downloadPoliciesCancellationListPDFAsync =
  (
    taskCode: number,
    // onCallback?: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/policy/get-cancellation-and-reinstatement-pdf?task_code=${taskCode}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = getFilenameFromContentDisposition(res);
      link.click();

      URL.revokeObjectURL(pdfUrl);
      // onCallback();
    } catch (err: any) {
      dispatch(fetchTasksListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertOldBusinessTrackerAysnc =
  (
    data: IOldBusinessTrackerState,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.put("/other/update-old-business-tracker", { ...data });
      dispatch(
        showMessage({
          type: "success",
          message: "Old Business Tracker saved succesfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_TASK = "CLEAR_TASK";
export const clearTaskCancel = () => action(CLEAR_TASK);
